import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Users, Briefcase, Cog, Snowflake, Fuel, DoorClosed, CheckCircle2, ArrowLeft } from "lucide-react";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = API.replace(/\/api$/, "");
  return u.startsWith("/api/") ? `${base}${u}` : u;
};

export default function VehicleDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/vehicles/${id}`); setV(r.data); }
      catch { setV(null); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#737373]">Loading vehicle…</div>;
  if (!v) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#737373]">Vehicle not found. <Link to="/fleet" className="text-[#D9A62E]">Back to fleet</Link></div>;

  const gallery = v.gallery?.length ? v.gallery : [v.primary_image];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <Link to="/fleet" className="inline-flex items-center gap-1.5 text-sm text-[#A3A3A3] hover:text-[#D9A62E] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to fleet
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
        <div className="lg:col-span-3">
          <div className="rounded-2xl overflow-hidden bg-[#0A0A0A] aspect-[16/10]">
            <img src={resolveUrl(gallery[active])} alt={v.name} className="w-full h-full object-cover" data-testid="vehicle-detail-gallery-image" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`rounded-lg overflow-hidden aspect-[16/10] border-2 transition-all ${active === i ? "border-[#D9A62E]" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={resolveUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <span className="inline-block bg-[#D9A62E]/10 text-[#D9A62E] border border-[#D9A62E]/30 text-[10px] px-3 py-1 rounded-full font-mono-piki font-semibold uppercase tracking-wider">{v.category}</span>
          <h1 className="mt-4 font-display text-3xl lg:text-4xl font-extrabold text-white">{v.name}</h1>
          <p className="mt-2 text-[#A3A3A3]">{v.tagline}</p>

          <div className="mt-6 flex items-baseline gap-2">
            <span className="text-4xl font-extrabold text-[#D9A62E] font-mono-piki">${v.daily_rate}</span>
            <span className="text-sm text-[#737373] font-mono-piki uppercase tracking-widest">/ day</span>
            <span className="ml-3 text-xs text-[#A3A3A3]">or ${v.weekly_rate}/week</span>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { icon: Users, label: `${v.passengers} passengers` },
              { icon: Briefcase, label: v.luggage },
              { icon: Cog, label: v.transmission },
              { icon: DoorClosed, label: `${v.doors} doors` },
              { icon: Snowflake, label: v.ac ? "Air Conditioning" : "No A/C" },
              { icon: Fuel, label: v.fuel },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2.5 p-3 rounded-lg bg-[#171717] border border-[#2E2E2E]">
                <s.icon className="w-4 h-4 text-[#D9A62E] shrink-0" />
                <span className="text-xs text-[#D4D4D4]">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-[#737373] font-mono-piki mb-3">Features & Inclusions</div>
            <ul className="space-y-2">
              {(v.features || []).map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[#D4D4D4]">
                  <CheckCircle2 className="w-4 h-4 text-[#D9A62E]" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <Link
            to={`/book?vehicle=${v.id}`}
            data-testid="vehicle-detail-book-enquiry-btn"
            className="mt-8 piki-gold-btn w-full text-center block"
          >
            Book This Vehicle
          </Link>
          <p className="mt-3 text-[11px] text-center text-[#737373]">Rental terms & availability confirmed after enquiry.</p>
        </div>
      </div>
    </div>
  );
}
